import { scalingPx } from 'util/css';
import { pxToEm } from 'util/pxToEm';
import { Box, IconButton, InputBase, Paper, styled } from '@mui/material';
import React, { FormEvent, ReactElement, ReactNode, useRef } from 'react';
import { SearchIcon } from 'components/icons';
import { Divider } from '../Divider';

export interface SearchBarProps {
  autoFocus?: boolean;
  className?: string;
  children?: ReactNode;
  onFocus?: () => void;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
}

export const SearchBar = styled(
  (props: SearchBarProps): ReactElement => {
    const hasChildren =
      React.Children.toArray(props.children).filter(Boolean).length > 0;

    const size = scalingPx(32, 40);

    const queryRef = useRef('');
    const radius = 4;

    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      <Paper
        square
        component="form"
        sx={{
          height: size,
          borderRadius: radius,
          borderBottomLeftRadius: hasChildren ? 0 : radius,
          borderBottomRightRadius: hasChildren ? 0 : radius,
          background: '#fff',
          marginRight: 32,
          minWidth: 260,
          width: 350,
          paddingLeft: 30,
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          '& .input': {
            width: '100%',
            '& .MuiInputBase-input': {
              fontFamily: 'Poppins Roboto "Sans Serif"',
              '&::placeholder': {
                color: theme => theme.palette.primary.main,
                opacity: 0.7,
              },
              fontSize: 13.5,
            },
          },
          '& .icon-button': {
            padding: 10,
            marginLeft: 'auto',
            color: theme => theme.palette.primary.main,
            fontWeight: 'bold',
          },
        }}
        className={props.className}
        style={{ paddingRight: 6 }}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          props.onSubmit?.(queryRef.current);
        }}
      >
        <InputBase
          name="search"
          placeholder="Search for schools or events"
          autoComplete="off"
          onChange={e => {
            queryRef.current = e.target.value;
            props.onChange?.(e.target.value);
          }}
          onFocus={props.onFocus}
          autoFocus={!!props.autoFocus}
          sx={{
            width: '100%',
            input: {
              fontSize: pxToEm(14),
              '&::placeholder': {
                color: '#a7a7a7',
                opacity: 1,
              },
            },
          }}
        />

        <IconButton
          type="submit"
          className="icon-button"
          aria-label="search"
          style={{ fontSize: 15, color: '#a7a7a7' }}
        >
          <SearchIcon />
        </IconButton>
        {hasChildren && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            boxShadow="0 3px 6px rgba(0,0,0,0.23)"
            style={{
              background: '#fff',
              borderBottomLeftRadius: radius,
              borderBottomRightRadius: radius,
            }}
          >
            <Divider size={1} />
            <Box>{props.children}</Box>
          </Box>
        )}
      </Paper>
    );
  },
)``;
